import { usePrintDocument } from '@Hooks/Documents';
import { type ApiPrintOption } from '@Model';
import { PrintOptionsModal } from '@Modules/Documents';
import { Button, defaultIconSize, FloatingCard, useTranslate } from '@synqo/frontend-component-library';
import { IconDownload, IconEye, IconFileText } from '@tabler/icons-react';
import { useState } from 'react';
import classes from './PortalRequisitionDocumentsCard.module.css';

// @ai-generated 2026-09-03T10:52:00+00:00 by philipp.kammerer@synqony.com using claude-opus-5
const optionKey = (option: ApiPrintOption) => `${option.id}-${option.related_object_id}`;

export interface PortalRequisitionDocumentsCardProps {
    printOptions: ApiPrintOption[];
    /** Puts the card on "loading", so "no documents" does not flash before the answer arrives. */
    isLoading?: boolean;
}

/** The documents of one order: a row per document, with a preview and a download of its own. */
// @ai-generated 2026-09-03T02:56:03+00:00 by philipp.kammerer@synqony.com using claude-opus-5
export function PortalRequisitionDocumentsCard({ printOptions, isLoading }: PortalRequisitionDocumentsCardProps) {
    const i18n = useTranslate('');
    const printDocument = usePrintDocument();
    const [currentPrintOption, setCurrentPrintOption] = useState<ApiPrintOption>();

    // Two orders of one requisition can offer the same document, so the file name tells them apart.
    const label = (option: ApiPrintOption, verb: string) =>
        [option.display_string, option.file_name, i18n(verb)].filter(Boolean).join(' ');

    const count = printOptions.length;
    const documentCount = `${count} ${i18n(count === 1 ? 'Dokument' : 'Dokumente')}`;

    return (
        <FloatingCard
            title={i18n('Belege')}
            additionalHeaderItems={count > 0 ? <span className={classes.count}>{documentCount}</span> : undefined}
            collapsible={false}
        >
            {/* The wait is said where the answer will stand, so the card is never blank without a word. */}
            {isLoading ? (
                <p className={classes.note}>{i18n('Belege werden geladen …')}</p>
            ) : count === 0 ? (
                <p className={classes.note}>{i18n('Es liegen noch keine Belege vor.')}</p>
            ) : (
                printOptions.map((option) => (
                    <div key={optionKey(option)} className={classes.document}>
                        <span className={classes.icon} aria-hidden>
                            <IconFileText size={18} />
                        </span>
                        <span className={classes.name}>{option.display_string}</span>
                        <Button
                            appearance="transparent"
                            label={label(option, 'ansehen')}
                            tooltip={i18n('Ansehen')}
                            onClick={() => setCurrentPrintOption(option)}
                        >
                            <IconEye size={defaultIconSize} />
                        </Button>
                        <Button
                            appearance="transparent"
                            label={label(option, 'herunterladen')}
                            tooltip={i18n('Herunterladen')}
                            onClick={() => {
                                printDocument({
                                    printOption: option.id,
                                    primaryId: option.related_object_id,
                                    mode: 'download',
                                });
                            }}
                        >
                            <IconDownload size={defaultIconSize} />
                        </Button>
                    </div>
                ))
            )}

            <PrintOptionsModal printOption={currentPrintOption} onClose={() => setCurrentPrintOption(undefined)} />
        </FloatingCard>
    );
}
